import { sortParam } from './sort.js';
import { z } from 'zod';
export const paginationQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
});
// ---------------------------------------------------------------------------
// Anmeldung
// ---------------------------------------------------------------------------
/**
 * Mandant bei der Anmeldung (KON-116): die 7-stellige Mandantennummer, Leerzeichen dürfen dazwischen stehen („482 9173“). Im Übergang nimmt der Server bei
 * bestehenden Mandanten auch noch die alte Firmenkennung an, deshalb ist sie hier als Form erlaubt. Ob sie gilt, entscheidet der Server.
 */
export const tenantKeyInput = z
    .string()
    .transform((v) => v.replace(/\s+/g, '').toLowerCase())
    .pipe(z.string().regex(/^(\d{7}|[a-z0-9-]{2,40})$/, 'Bitte die 7-stellige Mandantennummer angeben'));
export const loginInput = z.object({
    tenant: tenantKeyInput,
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
    password: z.string().min(1, 'Passwort fehlt').max(1024),
    /** "cookie": Token nur als HttpOnly-Cookie (Browser). "token": Token im JSON (App, Werkzeuge). */
    mode: z.enum(['cookie', 'token']).default('token'),
    /** "app": Sitzung der Wirkia App auf dem Handy, sie verlängert sich bei Benutzung (nur mit mode "token"). "web": Sitzung des Backoffice. */
    client: z.enum(['web', 'app']).optional(),
    deviceName: z.string().trim().max(100).optional(),
});
export const USER_STATUS_LABELS = {
    active: 'Aktiv',
    invited: 'Eingeladen',
    deactivated: 'Deaktiviert',
};
/** So viele zusätzliche Rollen kann ein Benutzer neben seiner Hauptrolle haben */
export const MAX_EXTRA_ROLES = 5;
export const roleKeyInput = z.string().trim().regex(/^[a-z0-9_-]{2,40}$/, 'Ungültige Rolle');
export const inviteUserInput = z.object({
    name: z.string().trim().min(2, 'Name fehlt').max(120),
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
    roleKey: roleKeyInput,
    /** Weitere Rollen, zum Beispiel Dispatcher und Fahrer für denselben Mitarbeiter */
    extraRoleKeys: z.array(roleKeyInput).max(MAX_EXTRA_ROLES, `Höchstens ${MAX_EXTRA_ROLES} zusätzliche Rollen`).default([]),
    /** Zusätzlich zum Link eine E-Mail mit dem Einladungslink an den Benutzer schicken */
    sendMail: z.boolean().default(false),
});
export const updateUserInput = z
    .object({
    name: z.string().trim().min(2, 'Name fehlt').max(120).optional(),
    roleKey: roleKeyInput.optional(),
    /** Ersetzt die zusätzlichen Rollen. Leer entfernt sie alle. */
    extraRoleKeys: z.array(roleKeyInput).max(MAX_EXTRA_ROLES, `Höchstens ${MAX_EXTRA_ROLES} zusätzliche Rollen`).optional(),
    /** false deaktiviert den Benutzer und beendet seine Sitzungen, true aktiviert ihn wieder */
    isActive: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Keine Änderung angegeben');
export const renewInvitationInput = z.object({ sendMail: z.boolean().default(false) });
export const MIN_PASSWORD_LENGTH = 12;
export const acceptInvitationInput = z.object({
    password: z.string().min(MIN_PASSWORD_LENGTH, `Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`).max(200, 'Das Passwort ist zu lang.'),
});
// ---------------------------------------------------------------------------
// Passwort ändern und zurücksetzen
// ---------------------------------------------------------------------------
/** Neues Passwort mit denselben Regeln wie bei der Einladung */
export const newPasswordInput = z.object({
    password: z.string().min(MIN_PASSWORD_LENGTH, `Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`).max(200, 'Das Passwort ist zu lang.'),
});
export const requestPasswordResetInput = z.object({
    tenant: tenantKeyInput,
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
});
export const changePasswordInput = z
    .object({
    currentPassword: z.string().min(1, 'Das aktuelle Passwort fehlt.').max(1024),
    newPassword: z.string().min(MIN_PASSWORD_LENGTH, `Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`).max(200, 'Das Passwort ist zu lang.'),
})
    .refine((v) => v.newPassword !== v.currentPassword, { message: 'Das neue Passwort muss sich vom aktuellen unterscheiden.', path: ['newPassword'] });
/** Eigenen Namen in „Mein Konto“ ändern (KON-123) */
export const updateProfileInput = z.object({
    name: z.string().trim().min(2, 'Der Name braucht mindestens 2 Zeichen.').max(120, 'Der Name ist zu lang.'),
});
/** Eigene E-Mail-Adresse ändern (KON-123). Sie ist der Anmeldename, deshalb wird das aktuelle Passwort abgefragt. */
export const changeEmailInput = z.object({
    currentPassword: z.string().min(1, 'Das aktuelle Passwort fehlt.').max(1024),
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
});
// ---------------------------------------------------------------------------
// Anfragen
// ---------------------------------------------------------------------------
export const INQUIRY_STATUSES = ['new', 'in_progress', 'offer_sent', 'won', 'closed', 'rejected', 'cancelled', 'spam'];
export const INQUIRY_STATUS_LABELS = {
    new: 'Neu',
    in_progress: 'In Bearbeitung',
    offer_sent: 'Angebot gesendet',
    won: 'Beauftragt',
    closed: 'Abgeschlossen',
    rejected: 'Abgelehnt',
    cancelled: 'Vom Kunden storniert',
    spam: 'Spam',
};
const optionalText = (max) => z.string().trim().max(max).optional().transform((v) => (v === '' ? undefined : v));
/**
 * Neue Anfrage, z. B. von einer Kundenwebseite über einen API-Schlüssel.
 * Branchenspezifische Angaben (Umzugsadressen, Personenzahl, Rebsorte …) stehen in "fields".
 */
export const createInquiryInput = z.object({
    type: z.string().trim().regex(/^[a-z0-9_-]{2,40}$/, 'Ungültiger Anfragetyp'),
    source: z.string().trim().max(40).default('website'),
    externalId: optionalText(100),
    contact: z.object({
        name: z.string().trim().min(2, 'Name fehlt').max(120),
        email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
        phone: optionalText(40),
        company: optionalText(160),
        customerType: z.enum(['private', 'business']).optional(),
    }),
    message: optionalText(5000),
    preferredDate: z.iso.date().optional(),
    fields: z.record(z.string().max(60), z.unknown()).default({}),
    consentAt: z.iso.datetime({ offset: true }),
    /** Nur für Webseiten-Schlüssel: false schaltet die Eingangsbestätigung von Wirkia ab, wenn die Webseite selbst eine verschickt */
    sendConfirmation: z.boolean().optional(),
});
export const updateInquiryInput = z
    .object({
    status: z.enum(INQUIRY_STATUSES).optional(),
    assignedUserId: z.uuid().nullable().optional(),
    internalNote: z.string().max(5000).nullable().optional(),
    /** Zugeordneter Kontakt (Modul Kontakte, KON-048). null löst die Zuordnung. */
    contactId: z.uuid().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Keine Änderung angegeben');
export const listInquiriesQuery = paginationQuery.extend({
    status: z.enum(INQUIRY_STATUSES).optional(),
    type: z.string().max(40).optional(),
    q: z.string().trim().max(100).optional(),
    /** Nur Anfragen eines Kontakts (Modul Kontakte) */
    contactId: z.uuid().optional(),
    sort: sortParam(['reference', 'name', 'type', 'status', 'createdAt', 'updatedAt'], '-createdAt'),
});
// ---------------------------------------------------------------------------
// Dateianhänge zu Anfragen
// ---------------------------------------------------------------------------
/**
 * Erlaubte Dateitypen mit der Endung, unter der sie gespeichert werden. Die API erkennt den Typ am Inhalt der
 * Datei und nicht an dem, was der Absender behauptet. Die Werte entsprechen denen der Bären-Crew-Webseite.
 */
export const ATTACHMENT_TYPES = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'application/pdf': 'pdf',
};
/** Grenzen je Anfrage. Sie sind vorerst fest und sollen später je Mandant einstellbar werden (KON-042). */
export const ATTACHMENT_LIMITS = {
    maxFiles: 5,
    maxBytes: 5 * 1024 * 1024,
};
export const INQUIRY_SOURCE_LABELS = {
    website: 'die Webseite',
    phone: 'Telefon',
    email: 'E-Mail',
    manual: 'manuelle Erfassung',
    employee: 'Mein Konto',
};
// ---------------------------------------------------------------------------
// Anfragetypen und ihre Felder (je Mandant)
// ---------------------------------------------------------------------------
export const INQUIRY_FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'boolean', 'select', 'multiselect', 'email', 'phone'];
export const INQUIRY_FIELD_TYPE_LABELS = {
    text: 'Text',
    textarea: 'Mehrzeiliger Text',
    number: 'Zahl',
    date: 'Datum',
    boolean: 'Ja/Nein',
    select: 'Auswahl',
    multiselect: 'Mehrfachauswahl',
    email: 'E-Mail',
    phone: 'Telefon',
};
/** Schlüssel eines Felds, so wie die Webseite ihn unter "fields" sendet, z. B. fromAddress */
const fieldKey = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/, 'Feldschlüssel: Buchstabe am Anfang, dann Buchstaben, Ziffern oder _, höchstens 60 Zeichen');
export const inquiryFieldDefinition = z
    .object({
    key: fieldKey,
    label: z.string().trim().min(1, 'Beschriftung fehlt').max(80),
    type: z.enum(INQUIRY_FIELD_TYPES).default('text'),
    required: z.boolean().default(false),
    /** Einheit hinter Zahlen, z. B. "m²" */
    unit: z.string().trim().min(1).max(20).optional(),
    /** Nur bei select und multiselect: erlaubte Werte mit Beschriftung */
    options: z
        .array(z.object({ value: z.string().trim().min(1).max(60), label: z.string().trim().min(1).max(80) }))
        .max(100)
        .optional(),
})
    .refine((f) => (f.type !== 'select' && f.type !== 'multiselect') || (f.options?.length ?? 0) > 0, {
    message: 'Auswahlfelder brauchen mindestens eine Option',
    path: ['options'],
});
export const inquiryTypeInput = z.object({
    key: z.string().trim().regex(/^[a-z0-9_-]{2,40}$/, 'Ungültiger Anfragetyp'),
    label: z.string().trim().min(1, 'Bezeichnung fehlt').max(80),
    sortOrder: z.number().int().min(0).max(1000).default(0),
    /** Rollen, die Anfragen dieses Typs sehen dürfen. Leer: alle Rollen mit dem Recht „Anfragen ansehen“. Fehlt die Angabe, bleibt der bisherige Wert. Der Inhaber sieht immer alle. */
    visibleRoleKeys: z.array(roleKeyInput).max(30).optional(),
    fields: z
        .array(inquiryFieldDefinition)
        .max(50)
        .default([])
        .refine((fields) => new Set(fields.map((f) => f.key)).size === fields.length, 'Jeder Feldschlüssel darf nur einmal vorkommen'),
});
/** Neue Reihenfolge der Anfragetypen: alle Schlüssel, so wie sie stehen sollen */
export const reorderInquiryTypesInput = z.object({
    keys: z.array(z.string().trim().min(1).max(40)).min(1).max(100),
});
const emailCheck = z.email();
const TRUE_VALUES = new Set([true, 1, '1', 'true', 'on', 'ja', 'yes']);
const FALSE_VALUES = new Set([false, 0, '0', 'false', 'off', 'nein', 'no']);
/**
 * Prüft die Branchenfelder einer Anfrage gegen die Felddefinitionen ihres Typs und vereinheitlicht die Werte.
 * Formulare senden oft alles als Text. Aus "110" wird deshalb die Zahl 110, aus "on" der Wert true.
 * Leere Werte gelten als nicht angegeben. Felder ohne Definition bleiben unverändert erhalten.
 */
export function validateInquiryFields(definitions, input) {
    const values = {};
    const errors = {};
    const defined = new Map(definitions.map((d) => [d.key, d]));
    for (const [key, raw] of Object.entries(input)) {
        if (!defined.has(key) && raw !== null && raw !== undefined && raw !== '')
            values[key] = raw;
    }
    for (const def of definitions) {
        const raw = typeof input[def.key] === 'string' ? input[def.key].trim() : input[def.key];
        if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
            if (def.required)
                errors[`fields.${def.key}`] = `${def.label} fehlt`;
            continue;
        }
        const result = normalizeFieldValue(def, raw);
        if ('error' in result)
            errors[`fields.${def.key}`] = result.error;
        else
            values[def.key] = result.value;
    }
    return { values, errors };
}
function normalizeFieldValue(def, raw) {
    switch (def.type) {
        case 'number': {
            const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.replace(',', '.')) : NaN;
            return Number.isFinite(n) ? { value: n } : { error: `${def.label}: bitte eine Zahl angeben` };
        }
        case 'boolean': {
            const v = typeof raw === 'string' ? raw.toLowerCase() : raw;
            if (TRUE_VALUES.has(v))
                return { value: true };
            if (FALSE_VALUES.has(v))
                return { value: false };
            return { error: `${def.label}: bitte Ja oder Nein angeben` };
        }
        case 'date':
            return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) && !Number.isNaN(Date.parse(raw))
                ? { value: raw }
                : { error: `${def.label}: bitte ein Datum im Format JJJJ-MM-TT angeben` };
        case 'select': {
            const v = String(raw);
            return def.options?.some((o) => o.value === v) ? { value: v } : { error: `${def.label}: unbekannter Wert „${v}“` };
        }
        case 'multiselect': {
            const list = (Array.isArray(raw) ? raw : [raw]).map(String);
            const unknown = list.find((v) => !def.options?.some((o) => o.value === v));
            return unknown === undefined ? { value: [...new Set(list)] } : { error: `${def.label}: unbekannter Wert „${unknown}“` };
        }
        case 'email': {
            const v = String(raw).toLowerCase();
            return emailCheck.safeParse(v).success ? { value: v } : { error: `${def.label}: ungültige E-Mail-Adresse` };
        }
        default: {
            if (typeof raw !== 'string' && typeof raw !== 'number')
                return { error: `${def.label}: bitte Text angeben` };
            const v = String(raw);
            const max = def.type === 'textarea' ? 5000 : def.type === 'phone' ? 40 : 1000;
            return v.length <= max ? { value: v } : { error: `${def.label}: höchstens ${max} Zeichen` };
        }
    }
}
//# sourceMappingURL=api.js.map